/**
 * SafetyFilterPanel reads the Safety-tab form controls (added to views/map/index.php) into a single
 * typed config object consumed by the rest of the safety-routing code. It also keeps the range sliders'
 * live value labels in sync and emits a `safetyFiltersChanged` event so a re-plan can be offered.
 *
 * Control name/id contract (must match the template):
 *   - checkbox  name=safetyRouting        master toggle (auto-plan after a normal search)
 *   - range     name=maxCorridorDistance  km, secure parking max distance from the route
 *   - range     name=psrSpacing           km, desired spacing between secure parkings
 *   - checkbox  name=securityLevel[]      one per certification level, value = certification_level_id
 *   - checkbox  name=avoidIncidents       enable incident danger penalty
 *   - range     name=dangerRadius         km, incident danger radius around the route
 *   - select    name=incidentRecency      months ("" = all)
 *   - select    name=incidentMinSeverity  euros (0 = any)
 */

export class SafetyFilterPanel {
  constructor(root = document) {
    this.root = root;
    this._bindLiveLabels();
    this._bindChange();
  }

  /**
   * @returns {{
   *   enabled: boolean,
   *   maxCorridorDistance: number, psrSpacing: number, securityLevels: number[],
   *   avoidIncidents: boolean, dangerRadius: number,
   *   incidentRecencyMonths: ?number, incidentMinSeverity: number,
   * }} distances in metres.
   */
  getConfig() {
    const recency = this._value("incidentRecency", "");
    return {
      enabled: this._checked("safetyRouting"),
      maxCorridorDistance: this._km("maxCorridorDistance", 5),
      psrSpacing: this._km("psrSpacing", 50),
      securityLevels: this._checkedValues("securityLevel[]").map(Number),
      avoidIncidents: this._checked("avoidIncidents"),
      dangerRadius: this._km("dangerRadius", 5),
      incidentRecencyMonths: recency === "" ? null : Number(recency),
      incidentMinSeverity: Number(this._value("incidentMinSeverity", "0")) || 0,
    };
  }

  _bindLiveLabels() {
    [
      ["maxCorridorDistance", "maxCorridorDistanceValue"],
      ["psrSpacing", "psrSpacingValue"],
      ["dangerRadius", "dangerRadiusValue"],
    ].forEach(([name, labelId]) => {
      const input = this.root.querySelector(`input[name=${name}]`);
      const label = this.root.querySelector(`#${labelId}`);
      if (input && label) {
        const update = () => {
          label.textContent = `${input.value} km`;
        };
        update();
        input.addEventListener("input", update);
      }
    });
  }

  _bindChange() {
    const form = this.root.querySelector("#safetyFilterForm");
    if (form) {
      form.addEventListener("change", () => {
        window.dispatchEvent(new CustomEvent("safetyFiltersChanged", {}));
      });
    }
  }

  _input(name) {
    return this.root.querySelector(`input[name="${name}"]`) || this.root.querySelector(`[name="${name}"]`);
  }

  _checked(name) {
    const el = this._input(name);
    return !!(el && el.checked);
  }

  _value(name, fallback) {
    const el = this._input(name);
    return el && el.value !== "" ? el.value : fallback;
  }

  _km(name, fallbackKm) {
    const el = this._input(name);
    const km = el ? Number(el.value) : fallbackKm;
    return (Number.isFinite(km) ? km : fallbackKm) * 1000;
  }

  _checkedValues(name) {
    return [...this.root.querySelectorAll(`input[name="${name}"]:checked`)].map((el) => el.value);
  }
}
